import { FastAverageColor } from 'fast-average-color';

interface ColorResult {
    backgroundColor: string;
    textColor: string;
}

const fac = new FastAverageColor();

/**
 * Calculate relative luminance of a color using the WCAG formula
 * https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
const calculateLuminance = (r: number, g: number, b: number): number => {
    const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Determine if a color is light based on luminance threshold
 * Using a threshold of 0.5 instead of the library's default
 */
const isColorLight = (r: number, g: number, b: number): boolean => {
    const luminance = calculateLuminance(r, g, b);
    return luminance > 0.5;
};

/**
 * Uses fast-average-color library to analyze image elements.
 * Returns transparent background if extraction fails.
 */
export const analyzeImageColors = async (
    image: HTMLImageElement,
): Promise<ColorResult> => {
    try {
        const color = await fac.getColor(image);
        const [r, g, b] = color.value;
        const backgroundColor = `rgba(${r}, ${g}, ${b}, 0.9)`;

        // Use custom luminance calculation instead of library's isLight
        const textColor = isColorLight(r, g, b) ? '#000000' : '#ffffff';

        return { backgroundColor, textColor };
    } catch (error) {
        // Default to transparent background like normal event cards
        return {
            backgroundColor: 'transparent',
            textColor: '#000000',
        };
    }
};
